import { FC, useState, useCallback } from "react";
import {
  mergeStyles,
  IStackStyles,
  Text,
  Stack,
  ScreenWidthMinSmall,
  FontSizes,
} from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { HorizontalStack, VerticalStack } from "../../../components/Stack";
import { Projects } from "..";
import { ViewAll } from "../../../components/ViewAll";
import { useNavigate } from "react-router-dom";
import useIsSignedIn from "../../../hooks/useIsSignedIn";
import { NewProject } from "../../../components/modals/NewProject";
import { colorPallete } from "../../../assets/color";
import SearchBar from "../../../components/SearchBox";
import { Providers } from "@microsoft/mgt-element";

interface IProps {
  projectLimit: number;
  setProjectLimit: (limit: number) => void;
}

const styles = {
  headerStyle: {
    fontSize: "20px",
    color: colorPallete.black2,
    paddingLeft: "15px",
    textAlign: "start",
    lineHeight: "22px",
    fontWeight: "400",
    fontStyle: "normal",
  },
  IconStyle: {
    textAlign: "center",
    fontSize: "140px",
    color: colorPallete.btBlue,
    marginBottom: "4%",
  },
  IconDivStyle: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    border: "3px solid lightgrey",
    minHeight: "17.5rem",
    marginLeft: "1%",
    borderRadius: "8px",
  },
  ProjectTitleStackStyle: {
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    marginTop: "2%",
  },
  ProjectStackStyle: {
    width: "100%",
    alignItems: "center",
    minHeight: "19rem",
    backgroundColor: "",
    height: "20rem",
  },
  NewProjectStackStyle: {
    minWidth: "16rem",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "",
    paddingTop: "1%",
  },
  projectContainerStackStyle: {
    minWidth: ScreenWidthMinSmall,
    width: "82%",
    backgroundColor: "inherit",
  },
  primaryButton: {
    backgroundColor: colorPallete.btBlue,
    color: colorPallete.white,
    border: "1px solid transparent",
    padding: "5px 16px 5px",
    borderRadius: "8px",
    cursor: "pointer",
    justifySelf: "center",
    fontSize: "17px",
    transition: "background-color 0.3s ease",
    top: "3px",
    position: "relative" as const,
  },
  primaryButtonHover: {
    backgroundColor: colorPallete.white,
    color: colorPallete.btBlue,
    border: "1px solid #18468F",
  },
};

const verticalStackStyles: IStackStyles = {
  root: {
    marginBottom: "2%",
    paddingRight: "1%",
    width: "100%",
    minHeight: "20rem",
  },
};

export const ProjectStack = (props: IProps) => {
  const { projectLimit, setProjectLimit } = props;
  const [left, setLeft] = useState<number>(0);
  let distance = left;
  const [rightArrowVisible, setRightArrowVisible] = useState<boolean>(false);
  const [leftArrowVisible, setLeftArrowVisible] = useState<boolean>(false);
  const [arrowButtons, setArrowButtons] = useState<boolean>(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  function click() {
    navigate("project");
  }

  let isSignedIn = useIsSignedIn();
  const [isLoginButtonHovered, setIsLoginButtonHovered] = useState<boolean>(false);

  const handleLogin = useCallback(async () => {
    try {
      await Providers.globalProvider.login!();
    } catch (error: any) {
      console.error('Error during login:', error);
    }
  }, []);

  const Buttons = () => {
    return (
      <>
        {leftArrowVisible && (
          <Stack
            onClick={() => {
              if (left > 0) {
                let a = 0;
                setLeft((a -= 680));
              } else {
                setLeft((distance -= 400));
              }
            }}
            style={{
              backgroundColor: colorPallete.white,
              left: 3,
            }}
            className="Icons"
          >
            <img src={require("../../../assets/Icons/leftIcon.png")} />
          </Stack>
        )}
        {rightArrowVisible && (
          <Stack
            onClick={() => {
              if (left < 0) {
                let a = 0;
                setLeft((a += 680));
              } else {
                setLeft((distance += 400));
              }
            }}
            style={{
              backgroundColor: colorPallete.white,
              right: -2,
            }}
            className="Icons"
          >
            <img src={require("../../../assets/Icons/rightIcon.png")} />
          </Stack>
        )}
      </>
    );
  };
  return (
    <VerticalStack styles={verticalStackStyles}>
      <HorizontalStack style={styles.ProjectTitleStackStyle}>
        <Text
          className={mergeStyles(styles.headerStyle)}
          style={{ fontFamily: "Poppins" }}
        >
          {t("projects")}
        </Text>
        {isSignedIn && (
          <HorizontalStack>
            <SearchBar placeholder={t("search_project")} />
            <ViewAll onClickFunction={click} />
          </HorizontalStack>
        )}
      </HorizontalStack>
      <Stack className="ProjectStack">
        {isSignedIn ? (
          <>
            <NewProject
              projectLimit={projectLimit}
              setProjectLimit={setProjectLimit}
            />
            <Stack style={styles.projectContainerStackStyle}>
              <HorizontalStack
                style={{
                  width: "100%",
                  justifyContent: "left",
                  alignItems: "center",
                  backgroundColor: "inherit",
                  position: "relative",
                  paddingLeft: "0px",
                  left: "16px",
                }}
              >
                <Buttons />
                <HorizontalStack
                  style={{
                    width: "99%",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                  }}
                >
                  <Projects
                    left={left}
                    setRightArrowVisible={setRightArrowVisible}
                    setLeftArrowVisible={setLeftArrowVisible}
                    setArrowButtons={setArrowButtons}
                  />
                </HorizontalStack>
              </HorizontalStack>
            </Stack>
          </>
        ) : (
          <Stack style={styles.IconDivStyle}>
            <Text style={{ fontSize: FontSizes.size20,
              width: "auto",
              display: "flex",
              alignContent: "center",
              alignItems: "center",
              gap: "10px",
            }}>
              <button 
                onClick={handleLogin}
                onMouseEnter={() => setIsLoginButtonHovered(true)}
                onMouseLeave={() => setIsLoginButtonHovered(false)}
                style={{
                  ...styles.primaryButton,
                  ...(isLoginButtonHovered && styles.primaryButtonHover),
                }}
              >
                Log in
              </button> to view projects.
            </Text>
          </Stack>
        )}
      </Stack>
    </VerticalStack>
  );
};
